import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  IconButton,
  CircularProgress,
  FormControlLabel,
  Switch,
  makeStyles,
} from "@material-ui/core";
import {
  Close as CloseIcon,
  Send as SendIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  PollOutlined as PollIcon,
} from "@material-ui/icons";
import { toast } from "react-toastify";
import api from "../../services/api";

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: "#7C4DFF",
    color: "#fff",
    padding: theme.spacing(1, 2),
  },
  titleContent: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  content: {
    padding: theme.spacing(2),
    minWidth: 360,
  },
  field: {
    marginBottom: theme.spacing(2),
    width: "100%",
  },
  optionRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  previewBox: {
    backgroundColor: theme.palette.type === "dark" ? "#1a1a1a" : "#ede7f6",
    borderRadius: theme.spacing(1),
    padding: theme.spacing(2),
    marginTop: theme.spacing(1),
    border: `2px solid #7C4DFF`,
  },
  previewOption: {
    backgroundColor: theme.palette.type === "dark" ? "#333" : "#fff",
    border: `1px solid #7C4DFF`,
    borderRadius: 8,
    padding: theme.spacing(0.8, 1.5),
    marginBottom: theme.spacing(0.5),
    fontSize: "0.9rem",
  },
}));

const PollSendModal = ({ open, onClose, ticketId }) => {
  const classes = useStyles();
  const [title, setTitle] = useState("");
  const [options, setOptions] = useState(["", ""]);
  const [multiSelect, setMultiSelect] = useState(false);
  const [loading, setLoading] = useState(false);

  const validOptions = options.filter((o) => o.trim() !== "");
  const isValid = title.trim() !== "" && validOptions.length >= 2;

  const handleAddOption = () => {
    if (options.length >= 12) {
      toast.warn("Maximo de 12 opcoes");
      return;
    }
    setOptions([...options, ""]);
  };

  const handleRemoveOption = (index) => {
    if (options.length <= 2) return;
    setOptions(options.filter((_, i) => i !== index));
  };

  const handleOptionChange = (index, value) => {
    const updated = [...options];
    updated[index] = value;
    setOptions(updated);
  };

  const handleSend = async () => {
    if (!isValid) return;
    setLoading(true);
    try {
      await api.post(`/messages/${ticketId}`, {
        body: "",
        fromMe: true,
        isPrivate: "false",
        pollType: "poll",
        pollTitle: title.trim(),
        pollOptions: validOptions,
        pollMultiSelect: multiSelect,
      });
      toast.success("Enquete enviada!");
      onClose();
    } catch (err) {
      toast.error("Erro ao enviar enquete");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle disableTypography className={classes.dialogTitle}>
        <Box className={classes.titleContent}>
          <PollIcon />
          <Typography variant="h6">Criar Enquete</Typography>
        </Box>
        <IconButton size="small" onClick={onClose} style={{ color: "#fff" }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent className={classes.content}>
        <TextField
          label="Pergunta da enquete"
          variant="outlined"
          className={classes.field}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Ex: Qual o melhor horario para reuniao?"
          autoFocus
        />

        <Typography variant="subtitle2" style={{ marginBottom: 8 }}>
          Opcoes (minimo 2, maximo 12):
        </Typography>
        {options.map((opt, index) => (
          <Box key={index} className={classes.optionRow}>
            <TextField
              variant="outlined"
              size="small"
              fullWidth
              value={opt}
              onChange={(e) => handleOptionChange(index, e.target.value)}
              placeholder={`Opcao ${index + 1}`}
              inputProps={{ maxLength: 100 }}
            />
            {options.length > 2 && (
              <IconButton size="small" onClick={() => handleRemoveOption(index)}>
                <DeleteIcon fontSize="small" color="error" />
              </IconButton>
            )}
          </Box>
        ))}
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={handleAddOption}
          disabled={options.length >= 12}
          style={{ marginTop: 4, marginBottom: 16 }}
        >
          Adicionar opcao
        </Button>

        <FormControlLabel
          control={
            <Switch
              checked={multiSelect}
              onChange={(e) => setMultiSelect(e.target.checked)}
              color="primary"
            />
          }
          label="Permitir multiplas respostas"
        />

        {isValid && (
          <Box className={classes.previewBox}>
            <Typography variant="body2" style={{ fontWeight: 600, color: "#7C4DFF", marginBottom: 8 }}>
              Preview
            </Typography>
            <Typography variant="body1" style={{ fontWeight: 500, marginBottom: 8 }}>
              {title}
            </Typography>
            {validOptions.map((opt, i) => (
              <Box key={i} className={classes.previewOption}>
                {opt}
              </Box>
            ))}
            <Typography variant="caption" color="textSecondary" style={{ marginTop: 4 }}>
              {multiSelect ? "Multipla escolha" : "Escolha unica"}
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="default">Cancelar</Button>
        <Button
          onClick={handleSend}
          color="primary"
          variant="contained"
          disabled={!isValid || loading}
          startIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
        >
          Enviar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PollSendModal;
